import { subDays } from 'date-fns';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissions } from '@/features/auth';
import { toIsoDate } from '@/shared/components/date-range-picker';
import { MoneyWaterfall, type WaterfallStep } from '@/shared/components/money-waterfall';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import type { FinancialReport, FinancialRow, SettlementSummaryRow } from '../types/report.types';

const pct = (value: number | null) => (value === null ? '—' : `${value}%`);

/** The waterfall of one currency's totals, every figure as the API sent it. */
function waterfallSteps(total: FinancialRow): WaterfallStep[] {
  return [
    { label: 'Discounts given (already off collected)', amount: total.discounts, kind: 'note' },
    { label: 'Collected', amount: total.collected, kind: 'start' },
    { label: 'Channel fees', amount: total.channelFees, kind: 'minus' },
    { label: 'Net revenue', amount: total.netRevenue, kind: 'result' },
    { label: 'Provider cost', amount: total.actualCost, kind: 'minus' },
    { label: 'Gross profit', amount: total.grossProfit, kind: 'result' },
    { label: 'Management fee', amount: total.mgmtFee, kind: 'minus' },
    { label: 'Net profit', amount: total.netProfit, kind: 'result' },
    { label: 'Partner share', amount: total.partnerShare, kind: 'minus' },
    { label: 'Retained', amount: total.retained, kind: 'result', final: true },
  ];
}

/**
 * One card per currency: what customers paid, down to what we keep. Amounts in
 * different currencies are never added up.
 */
export function FinancialWaterfallCards({ totals }: { totals: readonly FinancialRow[] }) {
  if (totals.length === 0) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {totals.map((total) => {
        const currency = total.currencyCode ?? 'USD';
        const code = total.currencyCode ?? 'Unknown currency';
        return (
          <Card key={total.currencyCode ?? 'none'} className="gap-2 py-4">
            <CardHeader className="px-4">
              <CardTitle className="flex items-baseline justify-between gap-2 text-sm">
                <span>{code} money waterfall</span>
                <span className="text-muted-foreground text-xs font-normal">
                  {formatNumber(total.completedOrders)} completed · margin{' '}
                  {pct(total.realizedMarginPct)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 px-4">
              <MoneyWaterfall
                steps={waterfallSteps(total)}
                currency={currency}
                label={`${code} waterfall`}
              />
              {total.refundsCount > 0 && (
                <p className="text-muted-foreground text-xs">
                  {formatNumber(total.refundsCount)} refunds ·{' '}
                  {formatCurrency(total.refundsAmount, currency)} — collected after refunds{' '}
                  {formatCurrency(total.collectedAfterRefunds, currency)}
                </p>
              )}
              {total.pricedOrders < total.completedOrders && (
                <p className="text-status-warning text-xs">
                  {formatNumber(total.completedOrders - total.pricedOrders)} orders have no pricing
                  record: counted in collected, not in fees, cost or profit.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export function FinancialRefunds({ report }: { report: FinancialReport }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Refunds</CardTitle>
      </CardHeader>
      <CardContent>
        {report.refunds.length === 0 ? (
          <p className="text-muted-foreground text-sm">No refunds recorded in this period.</p>
        ) : (
          <ul className="divide-y text-sm">
            {report.refunds.map((refund) => (
              <li key={refund.currencyCode ?? 'none'} className="flex justify-between gap-2 py-1.5">
                <span>
                  {refund.currencyCode ?? 'Not linked to an order'} · {formatNumber(refund.count)}{' '}
                  refunds
                </span>
                <span className="tabular-nums">
                  {refund.currencyCode
                    ? formatCurrency(refund.amount, refund.currencyCode)
                    : formatNumber(refund.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What the period's completed orders should settle with each payment channel
 * (the net we expect to receive) and each country's partner (the share we
 * owe), and how much of it is stated, settled and outstanding.
 */
export function FinancialSettlement({ report }: { report: FinancialReport }) {
  const { can } = usePermissions();
  const canIssue = can('settlements.read') && can('settlements.issue');
  // A statement covers days that have ended: the link stops at yesterday.
  const latest = toIsoDate(subDays(new Date(), 1));
  const issueLink = (row: SettlementSummaryRow) => {
    const params = new URLSearchParams({
      type: row.kind,
      country: String(row.countryId),
      from: report.from.slice(0, 10),
      to: report.to.slice(0, 10) > latest ? latest : report.to.slice(0, 10),
    });
    if (row.channelCode) params.set('channel', row.channelCode);
    return `/settlements/new?${params.toString()}`;
  };

  if (!report.settlementAvailable) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Settlement</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          Settled amounts come from the payment gateways and are not connected yet. Group by payment
          channel to see what each channel should settle.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Settlement</CardTitle>
        {can('settlements.read') && (
          <Link
            to="/settlements"
            className="text-primary text-xs hover:underline"
            data-print="hide"
          >
            Open settlements
          </Link>
        )}
      </CardHeader>
      <CardContent>
        {report.settlement.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing to settle: no completed orders with a pricing record in this period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="text-xs" aria-label="Settlement">
              <TableHeader>
                <TableRow>
                  <TableHead>Kind</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Channel / partner</TableHead>
                  <TableHead className="text-end">Orders</TableHead>
                  <TableHead className="text-end">Expected</TableHead>
                  <TableHead className="text-end">In statements</TableHead>
                  <TableHead className="text-end">Settled</TableHead>
                  <TableHead className="text-end">Outstanding</TableHead>
                  <TableHead className="text-end">Not yet stated</TableHead>
                  {canIssue && (
                    <TableHead data-print="hide">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.settlement.map((row) => (
                  <TableRow
                    key={`${row.kind}-${row.countryId}-${row.channelCode ?? 'partner'}-${row.currencyCode}`}
                  >
                    <TableCell>
                      {row.kind === 'Channel' ? 'Channel (in)' : 'Partner (out)'}
                    </TableCell>
                    <TableCell>{row.countryName ?? `#${row.countryId}`}</TableCell>
                    <TableCell className="font-mono">
                      {row.channelCode ?? 'Partner share'}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatNumber(row.orders)}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(row.expected, row.currencyCode)}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(row.inStatements, row.currencyCode)}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(row.settled, row.currencyCode)}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(row.outstanding, row.currencyCode)}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(row.notYetStated, row.currencyCode)}
                    </TableCell>
                    {canIssue && (
                      <TableCell data-print="hide">
                        {row.notYetStated > 0 && (
                          <Link
                            to={issueLink(row)}
                            className="text-primary whitespace-nowrap hover:underline"
                          >
                            Issue statement
                          </Link>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
